import { Button, InfoIcon, Input, Modal, Select } from "@bp/ui-components";
import type { SelectOptionType } from "@bp/ui-components/dist/@bp/lib/Select/Select";
import { useState } from "react";
import type { MultiValue, SingleValue } from "react-select";
import { useMutation } from "urql";
import { graphql } from "../../../client/gql";
import { InfoBox } from "../../../components/InfoBox/InfoBox";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import css from "../orgList.module.scss";

const EditProfileModalUpdateProfile = graphql(`
  mutation EditProfileModal_UpdateProfile($where: ProfileWhere, $update: ProfileUpdateInput) {
    updateProfiles(where: $where, update: $update) {
      profiles {
        uuid
      }
    }
  }
`);

// Profile roles only (@bp/pim-auth-constants ProfileRoles). ACL/admin roles
// (ADMIN, *_ADMIN, …) are a separate concept and are not assigned here.
const ROLE_OPTIONS: SelectOptionType[] = [
  { value: "TEACHER", label: "Lehrkraft" },
  { value: "STUDENT", label: "Schüler:in" },
  { value: "PARENT", label: "Eltern" },
  { value: "OTHER", label: "Sonstige" },
];

export type EditableProfile = {
  uuid: string;
  firstName?: string | null;
  lastName?: string | null;
  /** Contact address of this profile (invitations go here); the login address lives on the account. */
  email?: string | null;
  /** Organization this profile belongs to — needed to scope the role edge. */
  orgUuid: string;
  /** Current org-directed role name (TEACHER/STUDENT/…), if any. */
  role?: string | null;
};

type Props = {
  profile: EditableProfile;
  onClose: () => void;
  onSaved: () => void;
};

export function EditProfileModal({ profile, onClose, onSaved }: Props) {
  const [firstName, setFirstName] = useState(profile.firstName ?? "");
  const [lastName, setLastName] = useState(profile.lastName ?? "");
  const [role, setRole] = useState(profile.role ?? "");
  const [email, setEmail] = useState(profile.email ?? "");
  const [busy, setBusy] = useState(false);
  const trimmedEmail = email.trim();
  const emailChanged = trimmedEmail !== (profile.email ?? "");
  const emailValid = trimmedEmail.length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  const [, updateProfile] = useMutation(EditProfileModalUpdateProfile);

  const save = async () => {
    setBusy(true);
    const update: {
      firstName: string;
      lastName: string;
      email?: string | null;
      roles?: {
        Organization: {
          disconnect: { where: { node: { uuid: string } } }[];
          connect: { edge: { names: string[] }; where: { node: { uuid: string } } }[];
        }[];
      };
    } = { firstName, lastName };
    if (emailChanged) {
      update.email = trimmedEmail || null;
    }
    // Replace the org-directed role: drop any existing edge to this org, set the new one.
    if (role && role !== (profile.role ?? "")) {
      update.roles = {
        Organization: [
          {
            disconnect: [{ where: { node: { uuid: profile.orgUuid } } }],
            connect: [{ edge: { names: [role] }, where: { node: { uuid: profile.orgUuid } } }],
          },
        ],
      };
    }
    const res = await updateProfile({ where: { uuid: profile.uuid }, update });
    setBusy(false);
    if (res.error) {
      showUserErrorToast({ text: "Speichern fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast("Profil aktualisiert");
    onSaved();
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      hideFooter={false}
      width="s"
      title="Profil bearbeiten"
      onRequestClose={onClose}
      footer={
        <div className={css.foot}>
          <span className={css.footHint}>Gilt nur für das Profil in dieser Organisation.</span>
          <div className={css.footActions}>
            <Button hierarchy="tertiary" onClick={onClose}>
              Abbrechen
            </Button>
            <Button hierarchy="primary" disabled={busy || !emailValid} onClick={() => void save()}>
              Speichern
            </Button>
          </div>
        </div>
      }
    >
      <div className={css.stack}>
        <Input
          name="firstName"
          label="Vorname"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <Input
          name="lastName"
          label="Nachname"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <Input
          name="profileEmail"
          type="email"
          label="E-Mail (Profil)"
          value={email}
          error={emailValid ? undefined : "Keine gültige E-Mail-Adresse"}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Select
          name="role"
          label="Rolle"
          options={ROLE_OPTIONS}
          value={ROLE_OPTIONS.find((o) => o.value === role) ?? null}
          isSearchable={false}
          onChange={(opt: MultiValue<SelectOptionType> | SingleValue<SelectOptionType>) =>
            setRole(String((opt as SelectOptionType).value))
          }
        />
        <InfoBox tone="primary" icon={<InfoIcon style={{ fontSize: 15 }} />}>
          <span style={{ fontSize: 12 }}>
            Login-Adresse, Passwort und 2FA liegen am Konto — dort über „Bearbeiten" änderbar. Die
            Profil-E-Mail ist die Kontaktadresse in dieser Organisation, an die Einladungen gehen.
          </span>
        </InfoBox>
      </div>
    </Modal>
  );
}
